import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, AlertTriangle, Target } from 'lucide-react';

/**
 * V3AuditBanner — floating dev-mode badge showing V3 coverage % on the
 * currently mounted surface. Click to highlight non-V3 DOM elements.
 *
 * ONLY mounts when:
 *   1. process.env.NODE_ENV === 'development' (webpack replaces this), OR
 *   2. localStorage['orka.audit.enable'] === 'true' (manual override for
 *      preview / QA environments).
 *
 * Zero runtime cost in production.
 */

const RAW_COLOR_RE = /\b(bg|text|border|ring|from|to|via)-(red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose|stone|slate|gray|zinc|neutral)-[0-9]{2,3}\b/;
const V3_TOKEN_RE = /\borka-(surface|text|border|bg|transition|focus)-[a-z-]+/;

function scanDom() {
  // Scan every rendered element's className string.
  const nodes = document.querySelectorAll('[class]');
  let total = 0;
  let compliant = 0;
  const violators = [];
  nodes.forEach((el) => {
    const cls = typeof el.className === 'string' ? el.className : (el.className?.baseVal || '');
    if (!cls) return;
    total += 1;
    const hasV3 = V3_TOKEN_RE.test(cls);
    const hasRaw = RAW_COLOR_RE.test(cls);
    if (hasV3 && !hasRaw) compliant += 1;
    else if (hasRaw) violators.push(el);
    else compliant += 0.5; // Neutral (no color class at all) → half credit
  });
  const pct = total === 0 ? 100 : Math.round((compliant / total) * 100);
  return { pct, total, violators };
}

export default function V3AuditBanner() {
  const [enabled, setEnabled] = useState(false);
  const [stats, setStats] = useState({ pct: 100, total: 0, violators: [] });
  const [highlighted, setHighlighted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const dev = process.env.NODE_ENV !== 'production';
    const optIn = (() => {
      try { return localStorage.getItem('orka.audit.enable') === 'true'; }
      catch { return false; }
    })();
    const was = (() => {
      try { return sessionStorage.getItem('orka.audit.dismiss') === 'true'; }
      catch { return false; }
    })();
    setDismissed(was);
    setEnabled(dev || optIn);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const rescan = () => setStats(scanDom());
    rescan();
    // light-touch rescan on nav/mount changes
    const t = setInterval(rescan, 2500);
    return () => clearInterval(t);
  }, [enabled]);

  const highlight = useCallback(() => {
    stats.violators.forEach((el) => {
      el.dataset.__orkaViolator = 'true';
      el.style.outline = '2px dashed #dc2626';
      el.style.outlineOffset = '1px';
    });
    setHighlighted(true);
  }, [stats.violators]);

  const clear = useCallback(() => {
    document.querySelectorAll('[data-__orka-violator="true"]').forEach((el) => {
      delete el.dataset.__orkaViolator;
      el.style.outline = '';
      el.style.outlineOffset = '';
    });
    setHighlighted(false);
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem('orka.audit.dismiss', 'true'); } catch { /* ignore */ }
    clear();
    setDismissed(true);
  };

  // Global handle for manual invocation
  useEffect(() => {
    window.__orkaAudit = () => {
      const s = scanDom();
      setStats(s);
      return s;
    };
    window.__orkaAuditHighlight = highlight;
    window.__orkaAuditClear = clear;
    return () => {
      delete window.__orkaAudit;
      delete window.__orkaAuditHighlight;
      delete window.__orkaAuditClear;
    };
  }, [highlight, clear]);

  if (!enabled || dismissed) return null;

  const { pct, total, violators } = stats;
  const status = pct >= 90 ? 'good' : pct >= 70 ? 'warn' : 'bad';
  const Icon = status === 'good' ? Check : status === 'warn' ? Target : AlertTriangle;
  const colorClass =
    status === 'good' ? 'orka-bg-success-soft orka-text-success'
    : status === 'warn' ? 'orka-bg-warning-soft orka-text-warning'
    : 'orka-bg-danger-soft orka-text-danger';

  return createPortal(
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 px-3 py-1.5 rounded-full border orka-border-default orka-surface-raised shadow-lg text-xs font-medium"
      style={{ boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}
      data-testid="v3-audit-banner"
      role="status"
      aria-label="V3 coverage audit"
    >
      <span className={`px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${colorClass}`}>
        <Icon className="w-3 h-3" />
        V3 coverage: <strong data-testid="v3-audit-pct">{pct}%</strong>
      </span>
      <span className="orka-text-tertiary text-[10px]" data-testid="v3-audit-stats">
        {violators.length} raw on {total} els
      </span>
      {!highlighted ? (
        <button
          type="button"
          onClick={highlight}
          disabled={violators.length === 0}
          className="text-[10px] px-2 py-0.5 rounded-full orka-bg-intent-soft orka-text-intent font-semibold disabled:opacity-40"
          data-testid="v3-audit-highlight"
        >
          Highlight
        </button>
      ) : (
        <button
          type="button"
          onClick={clear}
          className="text-[10px] px-2 py-0.5 rounded-full orka-border-default border orka-text-secondary"
          data-testid="v3-audit-clear"
        >
          Clear
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="orka-text-tertiary hover:orka-text-primary"
        aria-label="Dismiss audit banner"
        data-testid="v3-audit-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body,
  );
}
